import {
  OpaqueX25519Key,
  X25519_KEY_LENGTH,
  exportX25519PrivKey,
  exportX25519PubKey,
  exportX25519PubKeyBytes,
  importX25519PrivKey,
  importX25519PubKey,
  newX25519KeyPair,
  x25519DeriveShared,
} from '../internal/x25519';
import { SALT_SHARED } from '../salts';
import { Brand } from '../types';
import { ArrayOrBase64, deriveSecretFromSeed, loadArrayOrBase64 } from '../utils';

import { SymEncryptionKey, deriveSymEncryptionKey } from './symmetric';

export type SharedSecretPubKey = Brand<OpaqueX25519Key, 'SharedSecretPubKey'>;
export type SharedSecretPrivKey = Brand<OpaqueX25519Key, 'SharedSecretPrivKey'>;

export interface SharedSecretKeyPair {
  sharedPubKey: SharedSecretPubKey;
  sharedPrivKey: SharedSecretPrivKey;
}

export async function serializeSharedSecretPublicKey(
  epk: SharedSecretPubKey,
): Promise<Uint8Array<ArrayBuffer>> {
  return exportX25519PubKey(epk);
}

export async function serializeSharedSecretPrivateKey(
  epk: SharedSecretPrivKey,
): Promise<Uint8Array<ArrayBuffer>> {
  return exportX25519PrivKey(epk);
}

export async function loadSharedSecretPubKey(data: ArrayOrBase64): Promise<SharedSecretPubKey> {
  const raw = loadArrayOrBase64(data, X25519_KEY_LENGTH);
  return (await importX25519PubKey(raw)) as SharedSecretPubKey;
}

export async function loadSharedSecretPrivKey(data: ArrayOrBase64): Promise<SharedSecretPrivKey> {
  const raw = loadArrayOrBase64(data, X25519_KEY_LENGTH);
  return (await importX25519PrivKey(raw)) as SharedSecretPrivKey;
}

export async function newSharedSecretKeyPair(): Promise<SharedSecretKeyPair> {
  const { privateKey, publicKey } = await newX25519KeyPair();
  return {
    sharedPrivKey: privateKey as SharedSecretPrivKey,
    sharedPubKey: publicKey as SharedSecretPubKey,
  };
}

export async function deriveSharedSecretKeyPair(
  seed: Uint8Array<ArrayBuffer>,
  secondarySeed: Uint8Array<ArrayBuffer> | null,
  salt: string,
): Promise<SharedSecretKeyPair> {
  const scalar = await deriveSecretFromSeed(seed, secondarySeed, salt, X25519_KEY_LENGTH);
  const sharedPrivKey = await loadSharedSecretPrivKey(scalar);
  const sharedPubKey = await loadSharedSecretPubKey(await exportX25519PubKeyBytes(sharedPrivKey));
  return { sharedPubKey, sharedPrivKey };
}

export async function combineSharedSecret(
  sharedPrivKey: SharedSecretPrivKey,
  sharedPubKey: SharedSecretPubKey,
  salt: string,
): Promise<SymEncryptionKey> {
  const keyData = await x25519DeriveShared(sharedPrivKey, sharedPubKey);
  return deriveSymEncryptionKey(keyData, null, SALT_SHARED + salt);
}
